import { Component, OnInit } from '@angular/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ItemSetService } from 'src/app/cms-v2/entities/item-set/item-set-service/item-set.service';
import { MessageService } from 'primeng/api';
import { environmentOptions } from 'src/app/common/constants/constants';

@Component({
  selector: 'app-new-set-dialog',
  templateUrl: './new-set-dialog.component.html',
  styleUrls: ['./new-set-dialog.component.scss']
})
export class NewSetDialogComponent implements OnInit {
  newSetForm: FormGroup;
  loading = false;
  /** Label/value so the multi-select's filter works (CMS-6337). */
  readonly environmentOptions = environmentOptions;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fb: FormBuilder,
    private itemSetService: ItemSetService,
    private messageService: MessageService
  ) {
    this.newSetForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      internalName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      env: [[], [Validators.required]],
      enabled: [true]
    });
  }

  ngOnInit() {
    // Auto-populate internalName based on name
    this.newSetForm.get('name')?.valueChanges.subscribe(name => {
      if (name && !this.newSetForm.get('internalName')?.dirty) {
        const internalName = name.trim();
        this.newSetForm.patchValue({ internalName }, { emitEvent: false });
      }
    });
  }

  async createSet() {
    if (this.newSetForm.invalid) {
      this.markFormGroupTouched(this.newSetForm);
      return;
    }

    this.loading = true;
    
    try {
      const formValue = this.newSetForm.value;
      const createDto = {
        data: {
          name: formValue.name,
          internalName: formValue.internalName,
          env: formValue.env,
          enabled: formValue.enabled,
          items_ref: this.config.data?.selectedItems || []
        }
      };

      const response = await this.itemSetService.createItemSet(createDto);
      
      this.messageService.add({
        severity: 'success',
        summary: 'Set Created',
        detail: `Item set "${formValue.name}" created successfully`
      });

      this.ref.close(response.itemset);
      
    } catch (error: any) {
      console.error('Error creating item set:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Creation Failed',
        detail: error?.message || 'Failed to create item set'
      });
    } finally {
      this.loading = false;
    }
  }

  cancel() {
    this.ref.close();
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  // Form validation helpers
  isFieldInvalid(fieldName: string): boolean {
    const field = this.newSetForm.get(fieldName);
    return !!(field?.invalid && (field?.dirty || field?.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.newSetForm.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) {
        if (fieldName === 'env') {
          return 'At least one environment must be selected';
        }
        return `${fieldName} is required`;
      }
      if (field.errors['minlength']) {
        return `${fieldName} must be at least ${field.errors['minlength'].requiredLength} characters`;
      }
      if (field.errors['maxlength']) {
        return `${fieldName} cannot exceed ${field.errors['maxlength'].requiredLength} characters`;
      }
    }
    return '';
  }
}